import axios from "axios";
import axiosInstance from "./axiosConfig";

// ---------------------------------------------------------------------------
// Module-level interceptor setup
// Runs ONCE when this file is first imported — before any React component
// mounts. This ensures the Authorization header is always attached, even for
// API calls that happen immediately after login.
// ---------------------------------------------------------------------------

let isRefreshing = false;
let failedQueue = []; // queued requests waiting for a new token

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

// Uses plain axios (not axiosInstance) so it bypasses our interceptors
// and avoids an infinite loop when the refresh token itself is expired.
async function refreshTokenRequest() {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) throw new Error("No refresh token available");

  const url = `${axiosInstance.defaults.baseURL}/authentication/token/refresh/`;
  const body = { refresh: refreshToken.replace(/"/g, "") };

  const response = await axios.post(url, body, {
    headers: { "Content-Type": "application/json" },
  });

  return {
    token: response?.data?.access ?? "",
    refreshToken: response?.data?.refresh ?? "",
  };
}

// -- Request interceptor: attach token from localStorage on every request --
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token && !config._retry) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error("Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// -- Response interceptor: handle token refresh when server signals it --
axiosInstance.interceptors.response.use(
  async (response) => {
    const originalRequest = response.config;

    if (response?.data?.refresh_token && !originalRequest._retry) {
      return handleExpiredToken(originalRequest);
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error?.response?.data?.refresh_token && !originalRequest._retry) {
      return handleExpiredToken(originalRequest);
    }

    return Promise.reject(error);
  }
);

function handleExpiredToken(originalRequest) {
  // If a refresh is already in progress, queue this request
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    }).then((newToken) => {
      originalRequest._retry = true;
      originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
      return axiosInstance(originalRequest);
    });
  }

  isRefreshing = true;
  originalRequest._retry = true;

  return refreshTokenRequest()
    .then((refreshResponse) => {
      // Persist new tokens
      localStorage.setItem("token", refreshResponse.token);
      if (refreshResponse.refreshToken) {
        localStorage.setItem("refreshToken", refreshResponse.refreshToken);
      }

      // Update the original request header
      originalRequest.headers[
        "Authorization"
      ] = `Bearer ${refreshResponse.token}`;

      // Unblock queued requests
      processQueue(null, refreshResponse.token);

      return axiosInstance(originalRequest);
    })
    .catch((refreshError) => {
      // Refresh token is expired/invalid — log the user out
      processQueue(refreshError, null);

      // Clear tokens AND the persisted Redux state (persist:root).
      // Without clearing persist:root, redux-persist rehydrates with the
      // old token on the next page load, causing isAuth=true again and
      // the permission API fires in a loop.
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("persist:root");

      window.location.href = "/login";
      return Promise.reject(refreshError);
    })

    .finally(() => {
      isRefreshing = false;
    });
}

// ---------------------------------------------------------------------------
// React component — now just a passthrough wrapper.
// Kept for backward compatibility with existing usage in index.js:
//   <AxiosInterceptor><App /></AxiosInterceptor>
// ---------------------------------------------------------------------------
const AxiosInterceptor = ({ children }) => {
  return children;
};

export { AxiosInterceptor };

